import { Checkbox, Form, useFormApi } from "informedv4";
import * as React from "react";
import { v4 } from "uuid";
import { Phase, WebPortalClient, Study, VisitObligation } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { Dropdown } from "../../forms/informedv4/DropdownV4";
import { MultiSelect } from "../../forms/informedv4/MultiSelectV4";
import { TextBox } from "../../forms/informedv4/TextBoxV4";
import { Required, RequiredNotBlank } from "../../forms/validation/Required";
import Modal, { ModalSize } from "../../misc/components/modal";
import { ScheduleVisit } from "./VisitSchedule";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { VisitObligationEnum } from "../../misc/api/apiTypes";
import { MaxLength } from "../../forms/validation/MaxLength";
import { MaxLengthValidator } from "../../forms/validation/ConfigurableItemValidator";


export interface VisitConfigurationCoreProps {
    id: string;
    isCreate: boolean;
    possibleRequiredVisits: any[];
    onComplete: (session: ScheduleVisit) => void;
    onClone: () => void;
}

export interface VisitConfigurationProps {
    visitItem: ScheduleVisit;
    possibleRequiredVisits: any[];
    onComplete: (session: ScheduleVisit) => void;
    onClose: () => void;
    onClone: () => void;
}

interface VisitConfigurationForm {
    Label: string;
    Description?: string;
    Phase: string;
    Obligation: string;
    Disabled: boolean;
    CanSpanDays: boolean;
    CommentRequired: boolean;
    ConfirmationRequired: boolean;
    RequiredVisits: any[];
    BlockedByVisits: any[];
    Cohort: string;
}

export const VisitConfiguration = ({ visitItem, possibleRequiredVisits, onComplete, onClose, onClone }: VisitConfigurationProps) => {
    const initialFormValues: VisitConfigurationForm = {
        Label: visitItem?.label,
        Description: visitItem?.description,
        Phase: visitItem?.phase,
        Obligation: visitItem?.obligation ?? VisitObligationEnum.CanSkipWithConfirmation,
        Disabled: visitItem?.disabled ?? false,
        CanSpanDays: visitItem?.canSpanDays ?? false,
        CommentRequired: visitItem?.commentRequired ?? false,
        ConfirmationRequired: visitItem?.confirmationRequired ?? false,
        RequiredVisits: visitItem?.requiredVisits,
        BlockedByVisits: visitItem?.blockedByVisits,
        Cohort: visitItem?.cohort
    }

    return (
        <Modal show={true} header="Visit Configuration" size={ModalSize.Large} closeModalHandler={onClose} automationId="VisitConfigurationModal">
            <Form validateOn="submit" noValidate initialValues={initialFormValues as unknown as Record<string, unknown>}>
                <VisitConfigurationCore id={visitItem?.id ?? v4()} possibleRequiredVisits={possibleRequiredVisits} isCreate={!visitItem} onComplete={onComplete} onClone={onClone} />
            </Form>
        </Modal>
    );
}

export const GetReferenceOrStudyPhases = async (client: WebPortalClient, study: Study, phaseSetter: React.Dispatch<React.SetStateAction<Phase[]>>) => {
    const phasesResult = await client.getStudySchedulePhases(undefined, `studyId eq ${study.id}`);

    if (phasesResult.value.length === 0) {
        const referencePhases = await client.getPhases();
        phaseSetter(referencePhases.value);
    } else {
        phaseSetter(phasesResult.value.map(p => ({ id: p.phaseId, name: p.name })));
    }
}

export const VisitConfigurationCore = ({ id, isCreate, possibleRequiredVisits, onComplete, onClone }: VisitConfigurationCoreProps) => {
    const formApi = useFormApi();
    const [phases, setPhases] = React.useState<Phase[]>([]);
    const [obligations, setObligations] = React.useState<VisitObligation[]>([]);
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();

    React.useEffect(() => {
        const getData = async () => {
            GetReferenceOrStudyPhases(client.client, study, setPhases);

            const visitObligations = await client.client.getVisitObligations();
            setObligations(visitObligations.value);
        }

        getData();
    }, [client.client, study]);

    const onSubmit = async () => {
        formApi.validate();
        const state = formApi.getFormState();

        if (state.invalid)
            return;

        const visit: ScheduleVisit = {
            id: id,
            label: state.values.Label as string,
            obligation: state.values.Obligation as string,
            disabled: state.values.Disabled as boolean,
            canSpanDays: state.values.CanSpanDays as boolean,
            commentRequired: state.values.CommentRequired as boolean,
            confirmationRequired: state.values.ConfirmationRequired as boolean,
            description: state.values.Description as string,
            phase: state.values.Phase as string,
            requiredVisits: state.values.RequiredVisits as any[],
            blockedByVisits: state.values.BlockedByVisits as any[],
            sessions: [],
            cohort: state.values.Cohort as string
        }

        onComplete(visit);
    }

    return (
        <>
            <div className="modal-body">
                <div className="row">
                    <div className="form-group col-md-6 col-xs-12">
                        <label htmlFor="label">Label</label>
                        <TextBox name="Label" id="Label" field="Label" validate={v => RequiredNotBlank(v) ?? MaxLengthValidator(v as string, 40)}  />
                    </div>
                    <div className="form-group col-md-6 col-xs-6">
                        <label htmlFor="Phase">Phase</label>
                        <Dropdown
                            name="Phase"
                            id="Phase"
                            field="Phase"
                            validate={Required}
                        >
                            <option
                                key={"OptionBlank"}
                                value={[]}
                            ></option>
                            {phases.map(st => (
                                <option
                                    key={`Option${st.id}`}
                                    value={st.id}
                                >
                                    {st.name}
                                </option>
                            ))}
                        </Dropdown>
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-6 col-sm-6 col-xs-6" data-automationid="Obligation">
                        <label htmlFor="Obligation">Obligation</label>
                        <Dropdown name="Obligation" id="Obligation" field="Obligation">
                            {obligations.map(vo => (
                                <option
                                    key={`Option${vo.id}`}
                                    value={vo.id}
                                >
                                    {vo.name}
                                </option>
                            ))}
                        </Dropdown>
                    </div>
                    <div className="form-group col-md-6 col-xs-6">
                        <label htmlFor="Cohort">Cohort</label>
                        <TextBox name="Cohort" id="Cohort" field="Cohort" />
                    </div>
                </div>
                <div className="row">                   
                    <div className="form-group col-md-3 col-sm-3 col-xs-6">
                        <Checkbox name="Disabled" id="Disabled" field="Disabled" />&nbsp;
                        <label htmlFor="Disabled">Disabled</label>
                    </div>
                    <div className="form-group col-md-3 col-sm-3 col-xs-6">
                        <Checkbox name="CanSpanDays" id="CanSpanDays" field="CanSpanDays" />&nbsp;
                        <label htmlFor="CanSpanDays">Can Span Days</label>

                    </div>
                    <div className="form-group col-md-3 col-sm-3 col-xs-6">
                        <Checkbox name="CommentRequired" id="CommentRequired" field="CommentRequired" />&nbsp;
                        <label htmlFor="CommentRequired">Comment Required</label>
                    </div>
                    <div className="form-group col-md-3 col-sm-3 col-xs-6">
                        <Checkbox name="ConfirmationRequired" id="ConfirmationRequired" field="ConfirmationRequired" />&nbsp;
                        <label htmlFor="ConfirmationRequired">Confirmation Required</label>
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-6 col-xs-6">
                        <label htmlFor="RequiredVisits">Required Visits</label>
                        <MultiSelect
                            name="RequiredVisits"
                            id="RequiredVisits"
                            field="RequiredVisits"
                            options={possibleRequiredVisits}
                            labelKey="Label"
                            valueKey="Id"
                            multi={true}
                        />
                    </div>
                    <div className="form-group col-md-6 col-xs-6" data-automationid="BlockedByVisits">
                        <label htmlFor="BlockedByVisits">Blocked by Visits</label>
                        <MultiSelect
                            name="BlockedByVisits"
                            id="BlockedByVisits"
                            field="BlockedByVisits"
                            options={possibleRequiredVisits}
                            labelKey="Label"
                            valueKey="Id"
                            multi={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="Description">Description</label>
                        <TextBox name="Description" id="Description" field="Description" />
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                {!isCreate ? <button className="btn btn-primary btn-outline" onClick={onClone}>Clone</button> : null}
                <button className="btn btn-primary" onClick={onSubmit} data-automationid="VisitConfiguration_Save">Save</button>
            </div>
        </>
    );
}
